import { useQuery } from '@tanstack/react-query';
import type { AiInstructionProject } from '@/types/ai-instructions';
import { apiClient } from '@/lib/api-client';

interface InstructionProjectsResponse {
  projects: AiInstructionProject[];
}

export function useInstructionProjects(
  organizationId: string,
  contentId: string | null,
  // Scope the modal to the clicked DataTable row's category so its list matches
  // the cell count (KIR-3356). Three states: `undefined` = no scope (all
  // categories); `null` = the untyped (null-category) row → sent as
  // `fileCategoryNull=true`; string = that category.
  fileCategory: string | null | undefined
) {
  return useQuery<InstructionProjectsResponse, Error>({
    queryKey: [
      'ai-instruction-projects',
      organizationId,
      contentId,
      fileCategory,
    ],
    queryFn: async (): Promise<InstructionProjectsResponse> => {
      if (!contentId) {
        throw new Error('Content ID is required');
      }

      const response = await apiClient.get(
        `/organizations/${organizationId}/ai-instructions/${contentId}/projects`,
        {
          params:
            fileCategory === undefined
              ? undefined
              : fileCategory === null
                ? { fileCategoryNull: 'true' }
                : { fileCategory },
        }
      );

      return response as unknown as InstructionProjectsResponse;
    },
    enabled: !!organizationId && !!contentId,
  });
}
